import React from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import PropTypes from "prop-types";
import { useTranslation } from 'react-i18next';
import { COURSE_CATEGORIES, getCourseCategory } from "../../../LambdaFunctions/shared/courseCategories";
import { weekdayName } from '../../i18n/calendarLabels';
/* eslint-disable linebreak-style */
/* eslint-disable quotes */
import Input from "../common/Input";
import PolishDateInput from "../common/PolishDateInput";
import {STATUS} from "../common/Const";
import {calculateDisplayedErrors, minutesToTimeString, timeStringToMinutes} from "../common/Helpers";
import SelectInput from "../common/SelectInput";
import Checkbox from "../common/Checkbox";

function formatCategoryOption(category) {
  return <CategoryOption category={category} />;
}

function CategoryOption({ category }) {
  const { t } = useTranslation();
  return (
    <span><span aria-hidden="true" style={{ backgroundColor: category.color, display: 'inline-block', width: 14, height: 14, borderRadius: 3, marginRight: 8 }} />
      {t(category.labelKey)}
    </span>
  );
}
CategoryOption.propTypes = { category: PropTypes.object.isRequired };

function SchoolClassForm({id, classTypeId, dayOfWeek, startTime, teacherId, bookId, isRecurring, fromDate, tillDate, comment, allowGroupExpansion, publicLevel,
  publicCategoryId, levelOptions,
  classTypes, weekdays, teachers, books, touched, errors, onBlur, onChange, onSubmit, onEdit, onCancel, status, dateReadOnly, canEdit, submitLabel, cancelLabel, onAddStudent}) {
  const { t: tr, i18n } = useTranslation();
  const err = calculateDisplayedErrors(errors, touched, status);
  const disabled = status === STATUS.SUBMITTING;
  const readOnly = status === STATUS.VIEW || !canEdit;
  const selectedClassType = classTypes.find(value => value.id === classTypeId);
  const startTimeMinutes = timeStringToMinutes(startTime);
  const validStartTime = /^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(startTime);
  const endTime = (selectedClassType && validStartTime) ? minutesToTimeString(startTimeMinutes + selectedClassType.length) : "";
  return (
    <div>
      <h2 className="manage-title">{tr("common:classes")}</h2>
      {id && !readOnly && <p className="text-muted">{tr("manage:classTypeScope")}</p>}
      <form id="group-form" onSubmit={onSubmit}>
        <div className="form-row d-fl">
          <div className="col-xl-2 col-md-5 mb-1  ">
            <SelectInput
              label={tr("manage:dayOfTheWeek_223")}
              onBlur={onBlur}
              onChange={onChange}
              name="dayOfWeek"
              value={dayOfWeek}
              error={err.dayOfWeek}
              readOnly={readOnly}
              defaultOption="Wybierz dzień tygodnia"
              options={weekdays.map(d => ({
                value: d.id,
                text: weekdayName(d.id, i18n.language),
              }))}
            />
          </div>
          <div className="col-xl-3 col-md-7 mb-1  ">
            <SelectInput
              label={tr("manage:type_129")}
              onBlur={onBlur}
              onChange={onChange}
              name="classTypeId"
              value={classTypeId}
              error={err.classTypeId}
              readOnly={readOnly}
              defaultOption="Wybierz typ zajęć"
              options={classTypes.map(classType => ({
                value: classType.id,
                text: classType.name,
              }))}
            />
          </div>
          <div className="col-xl-2 col-md-2 mb-1  ">
            <Input
              label={tr("manage:start_305")}
              onBlur={onBlur}
              type="text"
              pattern="([01][0-9]|2[0-3]):[0-5][0-9]"
              maxLength={5}
              onChange={onChange}
              name="startTime"
              value={startTime}
              error={err.startTime}
              placeholder="HH:mm"
              readOnly={readOnly}
            />
          </div>
          <div className="col-xl-2 col-md-2 mb-1  ">
            <Input
              label={tr("manage:end_306")}
              onBlur={onBlur}
              type="text"
              onChange={onChange}
              name="endTime"
              value={endTime}
              error={err.endTime}
              placeholder="HH:mm"
              readOnly
            />
          </div>
          <div className="col-xl-3 col-md-8 mb-1  ">
            <SelectInput
              label={tr("manage:teacher_226")}
              onBlur={onBlur}
              onChange={onChange}
              name="teacherId"
              value={teacherId}
              error={err.teacherId}
              readOnly={readOnly}
              defaultOption="Wybierz nauczyciela"
              options={teachers.map(t => ({
                value: t.id,
                text: `${t.firstName} ${t.lastName}`,
              }))}
            />
          </div>
        </div>
        <div className="form-row d-flex align-items-center">
          <div className="col-xl-3 col-md-4 col-sm-8 mb-1">
            <Checkbox
              label={tr("manage:recurringClasses_307")}
              onBlur={onBlur}
              onChange={onChange}
              name="isRecurring"
              isChecked={isRecurring}
              readOnly={readOnly}
              error={err.isRecurring}
            />
          </div>
          <div className="col-xl-2 col-md-4 mb-1  ">
            <PolishDateInput
              label={isRecurring ? tr("manage:classesFrom") : tr("manage:date_8")}
              onBlur={onBlur}
              onChange={onChange}
              name="fromDate"
              value={fromDate}
              readOnly={readOnly || dateReadOnly}
              error={err.fromDate}
            />
          </div>

          <div className="col-xl-2 col-md-4 mb-1  ">
            {isRecurring
              ? (
                <PolishDateInput
                  label={tr("manage:classesUntil_308")}
                  onBlur={onBlur}
                  onChange={onChange}
                  name="tillDate"
                  value={tillDate}
                  readOnly={readOnly}
                  error={err.tillDate}
                />
              )
              : null}
          </div>

          <div className="col-xl-5 mb-1  ">
            <SelectInput
              label={tr("manage:book_71")}
              onBlur={onBlur}
              onChange={onChange}
              name="bookId"
              value={bookId}
              error={err.bookId}
              readOnly={readOnly}
              defaultOption="Wybierz książke"
              options={books.map(b => ({
                value: b.id,
                text: b.title,
              }))}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="col-xl-12 mb-1  ">
            <Input
              label={tr("manage:notes_10")}
              type="textarea"
              onBlur={onBlur}
              onChange={onChange}
              name="comment"
              value={comment}
              readOnly={readOnly}
              error={err.comment}
              placeholder={tr("manage:notes_10")}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="col-lg-4 align-self-center">
            <Checkbox
              name="allowGroupExpansion"
              label={tr('manage:allowGroupExpansion')}
              isChecked={allowGroupExpansion}
              onChange={onChange}
              readOnly={readOnly || disabled}
            />
          </div>
          <div className="col-lg-4 form-group">
            <label htmlFor="publicCategoryId"><strong>{tr('manage:publicCategory')}</strong></label>
            <Select
              inputId="publicCategoryId"
              name="publicCategoryId"
              isClearable
              value={getCourseCategory(publicCategoryId) || null}
              options={COURSE_CATEGORIES}
              getOptionValue={category => category.id}
              getOptionLabel={category => tr(category.labelKey)}
              formatOptionLabel={formatCategoryOption}
              onChange={category => onChange({ target: { name: 'publicCategoryId', value: category ? category.id : '' } })}
              onBlur={() => onBlur({ target: { name: 'publicCategoryId' } })}
              isDisabled={readOnly || disabled}
              placeholder={tr('manage:chooseCourseCategory')}
              aria-describedby="publicCategoryId-help"
            />
            <small id="publicCategoryId-help" className="form-text text-muted">{tr('manage:publicCategoryHelp')}</small>
          </div>
          <div className="col-lg-4 form-group">
            <label htmlFor="publicLevel"><strong>{tr('manage:publicLevel')}</strong></label>
            <CreatableSelect
              inputId="publicLevel"
              name="publicLevel"
              isClearable
              value={publicLevel ? { value: publicLevel, label: publicLevel } : null}
              options={levelOptions.map(value => ({ value, label: value }))}
              onChange={option => onChange({ target: { name: 'publicLevel', value: option ? option.value : '' } })}
              onBlur={() => onBlur({ target: { name: 'publicLevel' } })}
              isDisabled={readOnly || disabled}
              placeholder={tr('manage:chooseOrEnter')}
              formatCreateLabel={value => tr('manage:createLabel', { value })}
              noOptionsMessage={() => tr('manage:chooseOrEnter')}
              isValidNewOption={value => Boolean(value.trim()) && value.trim().length <= 80}
              aria-describedby="publicLevel-help"
              aria-invalid={Boolean(err.publicLevel)}
            />
            <small id="publicLevel-help" className="form-text text-muted">{tr('manage:publicLabelHelp')}</small>
            {err.publicLevel && <div className="text-danger">{err.publicLevel}</div>}
          </div>
        </div>
        <div className="pt-1 row m-0 mb-3 school-class-form-actions">
          {readOnly && canEdit && id && onAddStudent && (
            <div className="col-xl-block ml-auto">
              <button type="button" className="btn manage-button manage-teal-button btn-block" disabled={disabled} onClick={onAddStudent}>{tr("manage:addStudent_28")}</button>
            </div>
          )}
          {readOnly && canEdit && (
            <div className={id && onAddStudent ? "col-xl-block" : "col-xl-block ml-auto"} >
              <button type="button" className="btn manage-button manage-red-button btn-block " disabled={disabled} onClick={onEdit}>{tr("manage:modify_310")}</button>
            </div>
          )}
          {!readOnly && canEdit && (
            <div className="col-xl-block ml-auto" >
              <button type="submit" className="btn manage-button manage-red-button btn-block " disabled={disabled}>{submitLabel}</button>
            </div>
          )}
          <div className="col-xl-block pl-2 pr-2" >
            <button type="button" className="btn manage-button manage-teal-button btn-block " disabled={disabled} onClick={onCancel}>{cancelLabel || (readOnly || !id ? tr("common:back") : tr("common:cancel"))}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

SchoolClassForm.propTypes = {
  allowGroupExpansion: PropTypes.bool,
  publicLevel: PropTypes.string,
  publicCategoryId: PropTypes.string,
  levelOptions: PropTypes.arrayOf(PropTypes.string),
  submitLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  id: PropTypes.string,
  classTypeId: PropTypes.string,
  dayOfWeek: PropTypes.string,
  startTime: PropTypes.string,
  teacherId: PropTypes.string,
  bookId: PropTypes.string,
  isRecurring: PropTypes.bool,
  fromDate: PropTypes.string,
  tillDate: PropTypes.string,
  comment: PropTypes.string,
  classTypes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    length: PropTypes.number,
    maxStudents: PropTypes.number,
  })).isRequired,
  weekdays: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })).isRequired,
  teachers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  })).isRequired,
  books: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  })).isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.objectOf(PropTypes.bool).isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  dateReadOnly: PropTypes.bool,
  canEdit: PropTypes.bool,
  onAddStudent: PropTypes.func,
};

SchoolClassForm.defaultProps = {
  allowGroupExpansion: false,
  publicLevel: '',
  publicCategoryId: '',
  levelOptions: [],
  submitLabel: 'Zapisz',
  cancelLabel: '',
  id: undefined,
  classTypeId: "",
  bookId: "",
  dayOfWeek: "",
  startTime: "",
  teacherId: "",
  isRecurring: false,
  fromDate: "",
  tillDate: "",
  comment: "",
  dateReadOnly: false,
  canEdit: true,
  onAddStudent: null,
};

export default SchoolClassForm;
